import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, catchError, lastValueFrom, of } from 'rxjs';
import { CommonService } from './common.service';
import { ApiService } from './api.service';
import { Fees, Order, Origin, FormStatus, Status } from '../models/loanData.model';
import { HostConnectionService } from './host-connection.service';

@Injectable({ providedIn: 'root' })
export class OriginService {

  private hostService = inject(HostConnectionService);

  constructor(private http: HttpClient, public commonService: CommonService, private apiService: ApiService) { }

  getOriginLoanData(originId: string, partnerAccessToken: string): Observable<any> {
    const url = `${environment.API_URL}/${environment.ORIGIN_PATH}${originId}?partner_access_token=${partnerAccessToken}`;
    return this.http.get<any>(url);
  }

  async getLoanData(originId: string, partnerAccessToken: string = "string"): Promise<Origin | null> {
    const endPoint = `${environment.ORIGIN_PATH}${originId}?partnerAccessToken=${partnerAccessToken}`;

    try {
      const response = await lastValueFrom(this.apiService.getData(endPoint));

      if (response?.status === 200) {
        return response.body as Origin;
      }
      else if (response?.status === 401) {
        const userLogin = await this.hostService.openLoginForm();
        if (userLogin?.username && userLogin?.password) {
          const originContext = await this.hostService.refreshOrigin();
          const { id: originId, partnerAccessToken, transactionId: currentTransactionId } = originContext;
          console.log('authenticateUser parameters: ' + originId + ' ' + partnerAccessToken + ' ' + currentTransactionId);
          return await this.getLoanData(originId, partnerAccessToken);
        } else {
          console.warn('Credentials not provided by EPC logic form.', userLogin);
          await this.hostService.close();
        }
      }
      else {
        this.apiService.handleError(response);
      }
      return null;
    }
    catch (error) {
      return null;
    }
    finally {

    }
  }


  async getFees(transactionId: string): Promise<Fees[] | []> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.FEES_PATH}${transactionId}`;
      const response: any = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of([])))
        .toPromise();

      if (response?.status === 200) {
        return response.body as Fees[];
      }
      else {
        this.apiService.handleError(response);
      }

      return [];
    } catch (error) {
      return [];
    }
    finally {
      this.commonService.stopSpinner();
    }
  }

  async importFees(data: any, transactionId: string): Promise<any> {
    try {
      const endPoint = `${environment.FEES_PATH}${transactionId}`;
      const response: any = await this.apiService
        .postData(endPoint, data)
        .pipe(catchError((err) => { return of(null); }))
        .toPromise();

      return response;
    } catch (error: any) {
      return null;
    }
  }

  async getOrderStatus(transactionId: string): Promise<Status | null> {
    try {
      const endPoint = `${environment.STATUS_PATH}${transactionId}`;
      const response = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of(null)))
        .toPromise();

      if (response?.status === 200) {
        return response.body as Status;
      }
      else {
        this.apiService.handleError(response);
      }

      return null;
    }
    catch (error) {
      return null;
    }
    finally {
      //this.commonService.stopSpinner();
    }
  }

  async getTransactionStatus(transactionId: string): Promise<Status | null> {
    try {
      const endPoint = `${environment.TRANSACTION_STATUS_PATH}${transactionId}`;
      const response = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of(null)))
        .toPromise();

      if (response?.status === 200) {
        return response.body as Status;
      }
      else {
        this.apiService.handleError(response);
      }

      return null;
    }
    catch (error) {
      return null;
    }
    finally {
      //this.commonService.stopSpinner();
    }
  }

  async getDocuments(transactionId: string, fileId: number, orderNumber: string): Promise<any[]> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.DOCUMENTS_PATH}${transactionId}?FileId=${fileId.toString()}&fileNumber=${orderNumber}`;
      const response: any = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of([])))
        .toPromise();

      if (response?.status === 200) {
        return response.body as any[];
      }
      else {
        this.apiService.handleError(response);
      }

      return [];
    } catch (error) {
      return [];
    }
    finally {
      this.commonService.stopSpinner();
    }
  }

  async getEPCDocuments(transactionId: string, fileId: number, orderNumber: string): Promise<any[]> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.EPC_DOCUMENTS_PATH}${transactionId}?fileId=${fileId.toString()}&fileNumber=${orderNumber}`;
      const response: any = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of([])))
        .toPromise();

      if (response?.status === 200) {
        return response.body as any[];
      }
      else {
        this.apiService.handleError(response);
      }

      return [];
    } catch (error) {
      return [];
    }
    finally {
      this.commonService.stopSpinner();
    }
  }

  async getOrders(loanNumber: string, transactionId: string): Promise<Order[]> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.ORDERS_PATH}${loanNumber}?transactionId=${transactionId}`;
      const response: any = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of([])))
        .toPromise();

      if (response?.status === 200) {
        return response.body as Order[];
      }
      else {
        this.apiService.handleError(response);
      }

      return [];
    } catch (error) {
      return [];
    }
    finally {
      this.commonService.stopSpinner();
    }
  }

  async uploadDocuments(data: any): Promise<any> {
    try {
      const endPoint = `${environment.UPLOADDOCUMENTS_PATH}`;
      const response: any = await this.apiService
        .postData(endPoint, data)
        .pipe(catchError((err) => { return of(null); }))
        .toPromise();

      return response;
    } catch (error: any) {
      return null;
    }
  }

  async uploadToResware(data: any): Promise<any[]> {
    try {
      const endPoint = `${environment.UPLOADTORESWARE_PATH}`;
      const response: any = await this.apiService
        .postData(endPoint, data)
        .pipe(catchError((err) => { return of([]); }))
        .toPromise();

      return response;
    } catch (error: any) {
      return [];
    }
  }

  async getFormStatus(transactionId: string, apiEndPoint: string): Promise<FormStatus | null> {
    try {
      const endPoint = `integrations/${apiEndPoint}/${transactionId}`;
      const response = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of(null)))
        .toPromise();

      if (response?.status === 200) {
        return response.body as FormStatus;
      }
      else {
        this.apiService.handleError(response);
      }

      return null;
    }
    catch (error) {
      return null;
    }
    finally {
      //this.commonService.stopSpinner();
    }
  }

  async getFeesTransactionsStatus(transactionId: string): Promise<FormStatus | null> {
    try {
      const endPoint = `${environment.FEES_STATUS_PATH}${transactionId}`;
      const response = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of(null)))
        .toPromise();

      if (response?.status === 200) {
        return response.body as FormStatus;
      }
      else {
        this.apiService.handleError(response);
      }

      return null;
    }
    catch (error) {
      return null;
    }
    finally {
      //this.commonService.stopSpinner();
    }
  }

  async getOrderHistory(loanNumber: string, instanceId: string): Promise<any[]> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.ORDER_HISTORY_PATH}${loanNumber}?instanceId=${instanceId}`;
      const response: any = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of([])))
        .toPromise();

      if (response?.status === 200) {
        return response.body as any[];
      }
      else {
        this.apiService.handleError(response);
      }

      return [];
    } catch (error) {
      return [];
    }
    finally {
      this.commonService.stopSpinner();
    }
  }
  async getFeesHistory(loanNumber: string, instanceId: string): Promise<any[]> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.FEES_HISTORY_PATH}${loanNumber}?instanceId=${instanceId}`;
      const response: any = await this.apiService.getData(endPoint)
        .pipe(catchError(() => of([])))
        .toPromise();

      if (response?.status === 200) {
        return response.body as any[];
      }
      else {
        this.apiService.handleError(response);
      }

      return [];
    } catch (error) {
      return [];
    }
    finally {
      this.commonService.stopSpinner();
    }
  }

  async getDocumentsStatus(data: any, transactionId: string, isUploadDocuments: boolean): Promise<any[]> {
    try {
      let endPoint = `${environment.DOCUMENTS_DOWNLOAD_STATUS_PATH}${transactionId}`;
      if (isUploadDocuments) {
        endPoint = `${environment.DOCUMENTS_STATUS_PATH}${transactionId}`;
      }
      const response: any = await this.apiService
        .postData(endPoint, data)
        .pipe(catchError((err) => { return of([]); }))
        .toPromise();

      return response;
    } catch (error: any) {
      return [];
    }
  }

  async integrationsLog(data: any): Promise<any> {
    try {
      const endPoint = `${environment.INTEGRATIONS_LOG_PATH}`;
      const response: any = await this.apiService
        .postData(endPoint, data)
        .pipe(catchError((err) => { return of(null); }))
        .toPromise();

      return response;
    } catch (error: any) {
      return null;
    }
  }

  async cancelFeesTransaction(transactionId: string): Promise<boolean> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.FEES_PATH}${transactionId}/cancel`;
      const response: any = await lastValueFrom(this.apiService.putData(endPoint, null));

      return response ?? true;

    }
    catch (error) {
      return false;
    }
    finally {
      this.commonService.stopSpinner();
    }
  }

  async getProductService(): Promise<any[] | []> {
    try {
      this.commonService.startSpinner();
      const endPoint = `${environment.PRODUCTS_PATH}`;
      //const response: any = await this.apiService.getData(endPoint).pipe().toPromise();
      //if (response?.status === 200) {
      //  return response.body;
      //}
      //else {
      //  this.apiService.handleError(response);
      //}        
      return this.PRODUCT_LIST;
      //return [];
    } catch (error) {
      return [];
    }
    finally {
      this.commonService.stopSpinner();
    }
  }

  PRODUCT_LIST = [
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 1. Standard",
      productTypeID: 2,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 1. Standard (Reissue)",
      productTypeID: 34,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 2. FHA Loan",
      productTypeID: 11,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 3. VA Loan",
      productTypeID: 10,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 4. Reverse Mortgage",
      productTypeID: 12,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 5. Title Insurance Only",
      productTypeID: 8,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 6. Closing Only",
      productTypeID: 3,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 7. USDA Refinance",
      productTypeID: 25,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 8. HELOC - Second Mortgage",
      productTypeID: 29,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 9. Sub Escrow Refinance",
      productTypeID: 32,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " Other Refinance",
      productTypeID: 31,
      transactionType: "Refinance",
      transactionTypeID: 2
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " 1. Standard",
      productTypeID: 6,
      transactionType: "Purchase",
      transactionTypeID: 3
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " Property Reports",
      productTypeID: 5,
      transactionType: "Property Reports",
      transactionTypeID: 4
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " Full Search",
      productTypeID: 23,
      transactionType: "Insured",
      transactionTypeID: 7
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " Assumption Loan",
      productTypeID: 36,
      transactionType: "Assumptions",
      transactionTypeID: 9
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " Purchase",
      productTypeID: 38,
      transactionType: "Assumptions",
      transactionTypeID: 9
    },
    {
      addOnProducts: [],
      ancillaryProducts: [],
      productType: " Release Liability",
      productTypeID: 37,
      transactionType: "Assumptions",
      transactionTypeID: 9
    },
    {
      addOnProducts: null,
      ancillaryProducts: null,
      productType: "6. Closing Only",
      productTypeID: 7,
      transactionType: "Purchase",
      transactionTypeID: 3
    }
  ];

}
